import {
  Condition,
  Employer,
  type ProfileProgramMembership
} from "@9amhealth/cms-api";
import { TaskResponse } from "@9amhealth/openapi";
import type { SanityImageAssetDocument } from "@sanity/client";
import { Cubit } from "blac";
import { Blac } from "blac-next";
import Language from "src/constants/language";
import { dateLocal } from "src/lib/date";
import type { Category } from "src/lib/getEducationalContent";
import { DefaultCategory, JourneyName } from "src/lib/getEducationalContent";
import { getSupportedUserLanguage } from "src/lib/i18next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { AppRemoteConfigCubit } from "src/state/AppRemoteConfigCubit/AppRemoteConfigCubit";
import { KnownProgram } from "src/constants/programs";
import { TaskResponseKnown } from "src/state/TaskManagementBloc/TaskManagementBloc";
import { TrackEvent } from "src/state/Track/TrackCubit";
import { ProfileAttributesKeys } from "src/state/UserCubit/UserCubit";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { tracker, userState } from "src/state/state";

enum EducationalContentType {
  ARTICLE = "article",
  VIDEO = "video"
}

export type SanctuaryHealthVideoMedia = {
  path?: string;
  captions?: Caption[];
};

export type SanctuaryHealthPost = {
  description?: string;
  title?: string;
};

interface Srt {
  url?: string;
  path?: string;
  mimetype?: string;
}

export interface FeedCategory {
  slug: string;
  title: Category;
  id: string;
}

export interface FeedData {
  feed: EducationalContent[];
  categories: FeedCategory[];
  feedLength: number;
  language: Language;
}

export interface Feed {
  data: FeedData;
  params: {
    conditions: string[];
    conditionCodes: string[];
    enrolled: string;
    journey: string;
    language: string;
    now: string;
    program: string;
    tasks: string;
  };
}

export interface Caption {
  language?: string;
  srt?: Srt;
}

export type SanctuaryHealthVideo = {
  landscapeVideo?: SanctuaryHealthVideoMedia;
  vimeoId?: string;
  photoUrl?: string;
  post?: SanctuaryHealthPost;
  title?: string;
};

export interface ContentCategory {
  slug: string;
  title: Category;
  id: string;
}

export interface EducationalContent {
  _id: string;
  title?: string;
  description?: string;
  url?: string;
  image?: SanityImageAssetDocument;
  addedAtUtc?: string;
  type?: EducationalContentType;
  language?: string;
  categories: ContentCategory[];
  sanctuaryHealthVideo?: SanctuaryHealthVideo;
  className?: string;
  journey?: JourneyName;
}

interface EducationalContentInUserPreferences {
  sanityDocumentId: string;
  addedAtUtc: string;
  type: EducationalContentType;
}

type FeedStatus = "loading" | "loaded" | "error";

interface UserEducationalFeedBlocState {
  status: FeedStatus;
  articles: EducationalContent[];
  categories: Category[];
  hasMoreContent?: boolean;
}

export default class UserEducationalFeedBloc extends Cubit<UserEducationalFeedBlocState> {
  userClass?: number;

  private feedRequestSequence = 0;
  constructor() {
    super({
      status: "loading",
      articles: [],
      categories: []
    });
  }

  get userAssignedArticles(): Record<string, unknown> {
    const userPreferences = Blac.getBloc(UserPreferencesCubit);

    const userPreferencesState = userPreferences.state as unknown as Record<
      string,
      unknown
    >;

    const keys = Object.keys(userPreferencesState);
    // Get all data object keys that start with 'user.careteamassigned.educationalcontent.'
    const educationalContentKeys = keys.filter((key) =>
      key.startsWith(UserPreferenceKeys.userEducationalContent + ".")
    );
    // Create new object from data object that contains only filtered keys
    const educationalContentPreferences = educationalContentKeys.reduce(
      (preferenceObject: typeof userPreferencesState, key) => {
        preferenceObject[key] = userPreferencesState[key];
        return preferenceObject;
      },
      {}
    );
    return educationalContentPreferences;
  }

  get userJourneys() {
    const userPreferences = Blac.getBloc(UserPreferencesCubit);
    return userPreferences.state[UserPreferenceKeys.journeyEnrollment] ?? [];
  }

  loadEducationalContent = async (tasks: TaskResponseKnown[]) => {
    const requestId = ++this.feedRequestSequence;

    this.emit({ ...this.state, status: "loading" });

    try {
      const data = await this.fetchFeedData(tasks);

      // STALE GUARD (success): a newer request superseded us — drop result.
      if (requestId !== this.feedRequestSequence) {
        return;
      }

      const categoriesNames = data.categories.map(
        (category) => category.slug as Category
      );
      const allAvailableCategories = [DefaultCategory.all, ...categoriesNames];

      this.emit({
        ...this.state,
        status: "loaded",
        articles: data.feed,
        categories: allAvailableCategories
      });
    } catch (e: unknown) {
      // STALE GUARD (error): must also gate the error emit so a stale failed
      // request cannot clobber a newer in-flight request's loading state.
      if (requestId !== this.feedRequestSequence) {
        return;
      }

      reportErrorSentry(e);
      this.emit({ ...this.state, status: "error" });
    }
  };

  private fetchFeedData = async (
    tasks: TaskResponseKnown[]
  ): Promise<FeedData> => {
    const userPreferences = Blac.getBloc(UserPreferencesCubit);

    try {
      await userState.ensureProfileAttributesLoaded([
        ProfileAttributesKeys.medicalConditions,
        ProfileAttributesKeys.programMemberships
      ]);
    } catch (e: unknown) {
      reportErrorSentry(e);
    }

    let date = userPreferences.journeys[0]?.date;

    if (!date) {
      try {
        await userPreferences.loadUserPreferences();
      } catch (e: unknown) {
        reportErrorSentry(e);
      }
    }

    date = userPreferences.journeys[0]?.date;

    date ??= userState.programMemberships?.[0]?.start;

    if (!date) {
      date = dateLocal().toISOString();
      const journey = JourneyName.default;
      void userPreferences.updateUserPreferences({
        [UserPreferenceKeys.journeyEnrollment]: [
          {
            journey,
            date
          }
        ]
      });
      tracker.track(TrackEvent.UserEnrolledInJourney, {
        data: {
          journey,
          date
        }
      });
    }

    const programNames = userState.programMemberships
      ?.map((p) => (p.active ? p.program : false))
      .filter(Boolean);

    const activeConditions =
      userState.medicalConditions?.filter(
        (medicalCondition) => !medicalCondition.abatement
      ) ?? [];

    const conditionCodes = [
      ...new Set(
        activeConditions
          .map((medicalCondition) => medicalCondition.condition.icd10)
          .filter(Boolean)
      )
    ];

    // The feed only targets conditions we have content for (diabetes, high
    // blood pressure, ...). Free-text conditions the member typed themselves
    // carry neither an ICD-10 code nor a known name, so they are simply left
    // out of the request rather than reported (FEWEB-2663).
    const knownConditions = new Set<string>(Object.values(Condition));
    const conditionsNames = [
      ...new Set(
        activeConditions
          .map((medicalCondition) => medicalCondition.condition.name)
          .filter((name) => knownConditions.has(name))
      )
    ];

    const programs = [
      KnownProgram.ONBOARDING,
      KnownProgram.CARE,
      KnownProgram.PHYSICAL_ACTIVITY_JOURNEY,
      KnownProgram.MINDSET_JOURNEY,
      KnownProgram.NUTRITION_JOURNEY,
      KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W
    ];

    const completedTasks = tasks
      .filter(
        (task) =>
          (task.status === TaskResponse.status.COMPLETED ||
            task.status === TaskResponse.status.SKIPPED) &&
          programs.includes(task.program as KnownProgram)
      )
      .map((completedTask) => ({
        program: completedTask.program,
        group: completedTask.group,
        slug: completedTask.slug,
        completedAt: completedTask.completedAt ?? completedTask.updatedAt
      }));
    const availableTasks = tasks
      .filter((task) => task.status === TaskResponse.status.AVAILABLE)
      .map((availableTask) => ({
        program: availableTask.program,
        group: availableTask.group,
        slug: availableTask.slug
      }));
    const inProgressTasks = tasks
      .filter((task) => task.status === TaskResponse.status.IN_PROGRESS)
      .map((inProgressTask) => ({
        program: inProgressTask.program,
        group: inProgressTask.group,
        slug: inProgressTask.slug
      }));

    const educationalContentUserPreferences = (
      Object.values(
        this.userAssignedArticles
      ) as EducationalContentInUserPreferences[]
    ).map((item) => ({
      id: item.sanityDocumentId,
      date: dateLocal().toISOString()
    }));

    const userLanguage = getSupportedUserLanguage();
    const knownEmployers = new Set<string>(Object.values(Employer));
    const rawEmployerPartnerName =
      userState.state.profileAttributes?.[
        ProfileAttributesKeys.partnerEmployersCurrent
      ]?.partner.name;
    const employerPartnerName =
      rawEmployerPartnerName && knownEmployers.has(rawEmployerPartnerName)
        ? (rawEmployerPartnerName as Employer)
        : undefined;
    const payerPartnerName =
      userState.state.profileAttributes?.[
        ProfileAttributesKeys.partnerPayersCurrent
      ]?.partner.name;
    const rawPrimaryCondition =
      userState.state.profileAttributes?.[
        ProfileAttributesKeys.primaryCondition
      ];
    const primaryCondition = knownConditions.has(rawPrimaryCondition ?? "")
      ? rawPrimaryCondition
      : undefined;

    const data = await Blac.getBloc(
      AppRemoteConfigCubit
    ).loadEducationalContent({
      language: userLanguage,
      enrolled: date,
      tasks: {
        completed: completedTasks,
        available: availableTasks,
        inProgress: inProgressTasks
      },
      programs: programNames as ProfileProgramMembership[] | undefined,
      employerPartnerName,
      payerPartnerName: payerPartnerName,
      conditions: conditionsNames as Condition[],
      conditionCodes,
      primaryCondition: primaryCondition as Condition | undefined,
      curated: educationalContentUserPreferences
    });

    return {
      ...data,
      feed: data.feed.map(
        (item) =>
          ({
            ...item,
            _id:
              (item as { _id?: string; id?: string })._id ??
              (item as { id?: string }).id
          }) as unknown as EducationalContent
      )
    };
  };
}
